import jsPDF from 'jspdf';
import QRCode from 'qrcode';
import type { StudyPlan } from '@/types';

/**
 * Generate PDF from study plan with complete information
 */
export async function generateStudyPlanPDF(studyPlan: StudyPlan, legalText?: string | null): Promise<Blob> {

  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 20;
  let yPosition = margin;

  // Load and fade background image using canvas
  let fadedBackgroundImage: string | null = null;
  let bgImageWidth = 0;
  let bgImageHeight = 0;

  try {
    const response = await fetch('/background.png');
    if (!response.ok) {
      throw new Error(`Failed to fetch: ${response.status}`);
    }

    const blob = await response.blob();

    // Convert blob to data URL (to avoid CSP blob: URL restrictions)
    const reader = new FileReader();
    const dataUrl = await new Promise<string>((resolve, reject) => {
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Failed to read blob'));
      reader.readAsDataURL(blob);
    });

    // Load image from data URL
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = dataUrl;
    });

    // Store original dimensions (convert pixels to mm)
    // Assuming 96 DPI: 1 inch = 25.4mm, 96px = 25.4mm, so 1px = 0.264583mm
    // Scale to 60% of original size
    const pxToMm = 0.264583 * 0.60;
    bgImageWidth = img.width * pxToMm;
    bgImageHeight = img.height * pxToMm;

    // Create canvas to fade the image to watermark level
    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext('2d');

    if (ctx) {
      // Draw image with 8% opacity for watermark effect
      ctx.globalAlpha = 0.08;
      ctx.drawImage(img, 0, 0);

      fadedBackgroundImage = canvas.toDataURL('image/png');
    }
  } catch (error) {
    console.error('Could not load background image:', error);
  }

  // Load verification stamp image
  let verificationStamp: string | null = null;
  try {
    const response = await fetch('/verifisert.png');
    if (response.ok) {
      const blob = await response.blob();
      const reader = new FileReader();
      verificationStamp = await new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('Failed to read stamp'));
        reader.readAsDataURL(blob);
      });
    }
  } catch (error) {
    console.error('Could not load verification stamp:', error);
  }

  // Helper function to add tiled background to page
  const addBackground = () => {
    if (fadedBackgroundImage && bgImageWidth > 0 && bgImageHeight > 0) {
      // Calculate how many tiles we need horizontally and vertically
      const tilesX = Math.ceil(pageWidth / bgImageWidth);
      const tilesY = Math.ceil(pageHeight / bgImageHeight);

      // Draw tiles
      for (let y = 0; y < tilesY; y++) {
        for (let x = 0; x < tilesX; x++) {
          const xPos = x * bgImageWidth;
          const yPos = y * bgImageHeight;
          pdf.addImage(fadedBackgroundImage, 'PNG', xPos, yPos, bgImageWidth, bgImageHeight, undefined, 'FAST');
        }
      }
    }
  };

  // Helper function to check if we need a new page
  const checkNewPage = (requiredSpace: number) => {
    if (yPosition + requiredSpace > pageHeight - 30) {
      pdf.addPage();
      addBackground(); // Add background to new page
      yPosition = margin;
      return true;
    }
    return false;
  };

  // Add background to first page
  addBackground();

  // Header with KF branding
  pdf.setFillColor(3, 52, 89); // KF Primary color #033459
  pdf.rect(0, 0, pageWidth, 45, 'F');

  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(26);
  pdf.text('Kompetanseattest', margin, 22);
  pdf.setFontSize(13);
  pdf.text('Godkjent studieplan', margin, 33);

  // Verification stamp image
  if (verificationStamp) {
    const stampSize = 36; // Size of stamp in mm
    const stampX = pageWidth - margin - stampSize;
    const stampY = 7; // Top of header area
    pdf.addImage(verificationStamp, 'PNG', stampX, stampY, stampSize, stampSize);
  }

  yPosition = 55;
  pdf.setTextColor(0, 0, 0);

  // Title
  pdf.setFontSize(20);
  pdf.setFont('helvetica', 'bold');
  const titleLines = pdf.splitTextToSize(studyPlan.title, pageWidth - 2 * margin);
  pdf.text(titleLines, margin, yPosition);
  yPosition += titleLines.length * 10 + 5;

  // Description
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'normal');
  const splitDescription = pdf.splitTextToSize(studyPlan.description, pageWidth - 2 * margin);
  pdf.text(splitDescription, margin, yPosition);
  yPosition += splitDescription.length * 7 + 15;

  // Metadata boxes
  checkNewPage(25);
  const boxWidth = (pageWidth - 2 * margin - 15) / 4;
  const boxHeight = 18;
  const boxY = yPosition;

  // Kompetansepoeng
  pdf.setFillColor(245, 245, 245);
  pdf.roundedRect(margin, boxY, boxWidth, boxHeight, 2, 2, 'F');
  pdf.setFontSize(9);
  pdf.setTextColor(100, 100, 100);
  pdf.text('Kompetansepoeng', margin + 3, boxY + 6);
  pdf.setFontSize(16);
  pdf.setTextColor(3, 52, 89);
  pdf.setFont('helvetica', 'bold');
  pdf.text(studyPlan.ects_credits.toString(), margin + 3, boxY + 14);

  // Normativ studiebelasting
  pdf.setFillColor(245, 245, 245);
  pdf.roundedRect(margin + boxWidth + 5, boxY, boxWidth, boxHeight, 2, 2, 'F');
  pdf.setFontSize(9);
  pdf.setTextColor(100, 100, 100);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Normativ studiebelasting', margin + boxWidth + 8, boxY + 6);
  pdf.setFontSize(16);
  pdf.setTextColor(3, 52, 89);
  pdf.setFont('helvetica', 'bold');
  pdf.text(studyPlan.estimated_hours.toString() + ' timer', margin + boxWidth + 8, boxY + 14);

  // Validation Score
  pdf.setFillColor(245, 245, 245);
  pdf.roundedRect(margin + 2 * (boxWidth + 5), boxY, boxWidth, boxHeight, 2, 2, 'F');
  pdf.setFontSize(9);
  pdf.setTextColor(100, 100, 100);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Valideringsscore', margin + 2 * (boxWidth + 5) + 3, boxY + 6);
  pdf.setFontSize(16);
  pdf.setTextColor(40, 167, 69);
  pdf.setFont('helvetica', 'bold');
  pdf.text(`${Number(studyPlan.validation_score || 0).toFixed(1)}%`, margin + 2 * (boxWidth + 5) + 3, boxY + 14);

  // EQF Level
  pdf.setFillColor(245, 245, 245);
  pdf.roundedRect(margin + 3 * (boxWidth + 5), boxY, boxWidth, boxHeight, 2, 2, 'F');
  pdf.setFontSize(9);
  pdf.setTextColor(100, 100, 100);
  pdf.setFont('helvetica', 'normal');
  pdf.text('EQF-nivå', margin + 3 * (boxWidth + 5) + 3, boxY + 6);
  pdf.setFontSize(16);
  pdf.setTextColor(3, 52, 89);
  pdf.setFont('helvetica', 'bold');
  pdf.text(`Nivå ${studyPlan.metadata?.eqf_level || 'N/A'}`, margin + 3 * (boxWidth + 5) + 3, boxY + 14);

  yPosition += boxHeight + 20;

  // Kategorier (kompetanseheving.no)
  if (Array.isArray(studyPlan.categories) && studyPlan.categories.length > 0) {
    checkNewPage(20);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(13);
    pdf.setTextColor(0, 0, 0);
    pdf.text('Kategorier (kompetanseheving.no)', margin, yPosition);
    yPosition += 8;

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);

    const primary = studyPlan.categories[0];
    const others = studyPlan.categories.slice(1);

    pdf.setTextColor(100, 100, 100);
    pdf.text('Primær:', margin, yPosition);
    pdf.setTextColor(3, 52, 89);
    pdf.setFont('helvetica', 'bold');
    pdf.text(primary, margin + 18, yPosition);
    yPosition += 6;

    if (others.length > 0) {
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 100, 100);
      pdf.text('Øvrige:', margin, yPosition);
      pdf.setTextColor(0, 0, 0);
      const othersText = others.join(', ');
      const othersLines = pdf.splitTextToSize(othersText, pageWidth - margin - 20 - margin);
      pdf.text(othersLines, margin + 18, yPosition);
      yPosition += othersLines.length * 5 + 2;
    }
    pdf.setTextColor(0, 0, 0);
    yPosition += 12;
  }

  // Learning Outcomes Section
  checkNewPage(25);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(16);
  pdf.setTextColor(0, 0, 0);
  pdf.text('Læringsutbytte', margin, yPosition);
  yPosition += 12;

  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'normal');

  // Knowledge
  if (studyPlan.learning_outcomes.knowledge?.length) {
    checkNewPage(15);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Kunnskaper:', margin, yPosition);
    yPosition += 7;
    pdf.setFont('helvetica', 'normal');

    studyPlan.learning_outcomes.knowledge.forEach((item: string) => {
      checkNewPage(10);
      const split = pdf.splitTextToSize(`• ${item}`, pageWidth - 2 * margin - 5);
      pdf.text(split, margin + 5, yPosition);
      yPosition += split.length * 6 + 2;
    });
    yPosition += 5;
  }

  // Skills
  if (studyPlan.learning_outcomes.skills?.length) {
    checkNewPage(15);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Ferdigheter:', margin, yPosition);
    yPosition += 7;
    pdf.setFont('helvetica', 'normal');

    studyPlan.learning_outcomes.skills.forEach((item: string) => {
      checkNewPage(10);
      const split = pdf.splitTextToSize(`• ${item}`, pageWidth - 2 * margin - 5);
      pdf.text(split, margin + 5, yPosition);
      yPosition += split.length * 6 + 2;
    });
    yPosition += 5;
  }

  // Competence
  if (studyPlan.learning_outcomes.competence?.length) {
    checkNewPage(15);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Kompetanse:', margin, yPosition);
    yPosition += 7;
    pdf.setFont('helvetica', 'normal');

    studyPlan.learning_outcomes.competence.forEach((item: string) => {
      checkNewPage(10);
      const split = pdf.splitTextToSize(`• ${item}`, pageWidth - 2 * margin - 5);
      pdf.text(split, margin + 5, yPosition);
      yPosition += split.length * 6 + 2;
    });
    yPosition += 5;
  }

  // Content Modules
  if (studyPlan.content_modules?.length) {
    yPosition += 10;
    checkNewPage(25);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(16);
    pdf.text('Innholdsmoduler', margin, yPosition);
    yPosition += 12;

    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'normal');
    studyPlan.content_modules.forEach((module: string) => {
      checkNewPage(10);
      const split = pdf.splitTextToSize(`• ${module}`, pageWidth - 2 * margin - 5);
      pdf.text(split, margin + 5, yPosition);
      yPosition += split.length * 6 + 2;
    });
    yPosition += 5;
  }

  // Assessment Criteria
  if (studyPlan.assessment_criteria) {
    yPosition += 10;
    checkNewPage(25);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(16);
    pdf.text('Vurderingskriterier', margin, yPosition);
    yPosition += 12;

    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'normal');
    const criteriaLines = pdf.splitTextToSize(studyPlan.assessment_criteria, pageWidth - 2 * margin);
    criteriaLines.forEach((line: string) => {
      checkNewPage(8);
      pdf.text(line, margin, yPosition);
      yPosition += 6;
    });
    yPosition += 10;
  }

  // Study Association
  if (studyPlan.study_association) {
    yPosition += 10;
    checkNewPage(25);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(16);
    pdf.text('Studieforbund', margin, yPosition);
    yPosition += 12;

    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'normal');
    pdf.text(studyPlan.study_association.name, margin, yPosition);
    yPosition += 7;

    if (studyPlan.study_association.organization_number) {
      pdf.setFontSize(10);
      pdf.setTextColor(100, 100, 100);
      pdf.text(`Org.nr: ${studyPlan.study_association.organization_number}`, margin, yPosition);
      pdf.setTextColor(0, 0, 0);
      yPosition += 10;
    }
  }

  // Approval Information
  if (studyPlan.approved_at || studyPlan.approver) {
    yPosition += 10;
    checkNewPage(30);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(16);
    pdf.text('Godkjenningsinformasjon', margin, yPosition);
    yPosition += 12;

    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'normal');

    if (studyPlan.approved_at) {
      pdf.setTextColor(100, 100, 100);
      pdf.text('Godkjent dato:', margin, yPosition);
      pdf.setTextColor(0, 0, 0);
      pdf.text(new Date(studyPlan.approved_at).toLocaleDateString('no-NO', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }), margin + 45, yPosition);
      yPosition += 7;
    }

    if (studyPlan.approver) {
      pdf.setTextColor(100, 100, 100);
      pdf.text('Godkjent av:', margin, yPosition);
      pdf.setTextColor(0, 0, 0);
      const approverText = `${studyPlan.approver.full_name} etter fullmakt fra ${studyPlan.study_association?.name || 'studieforbundet'}`;
      const approverLines = pdf.splitTextToSize(approverText, pageWidth - margin - 45);
      pdf.text(approverLines, margin + 45, yPosition);
      yPosition += (approverLines.length * 6) + 10;
    }
  }

  // QR Code and Verification
  if (studyPlan.approval_link) {
    yPosition += 15;
    checkNewPage(60);

    const qrSize = 50;

    try {
      // Generate QR code directly in browser as base64 data URL
      const baseUrl = import.meta.env.VITE_BASE_URL || window.location.origin;
      const verificationUrl = `${baseUrl}/verify/${studyPlan.approval_link}`;

      const qrCodeDataUrl = await QRCode.toDataURL(verificationUrl, {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        },
        errorCorrectionLevel: 'M'
      });

      // Add QR code image to PDF
      pdf.addImage(qrCodeDataUrl, 'PNG', margin, yPosition, qrSize, qrSize);

      // Verification text next to QR
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('Skann for å verifisere', margin + qrSize + 10, yPosition + 15);

      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 100, 100);
      const baseUrlForDisplay = (import.meta.env.VITE_BASE_URL || window.location.origin).replace(/^https?:\/\//, '');
      const urlText = `${baseUrlForDisplay}/verify/${studyPlan.approval_link}`;
      const urlLines = pdf.splitTextToSize(urlText, pageWidth - margin - qrSize - 15);
      pdf.text(urlLines, margin + qrSize + 10, yPosition + 25);

      // Update yPosition to be below QR code
      yPosition += qrSize + 10;
    } catch (error) {
      // Add fallback text if QR code fails
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('Verifiser online:', margin, yPosition);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(3, 52, 89);
      const baseUrlForDisplay = (import.meta.env.VITE_BASE_URL || window.location.origin).replace(/^https?:\/\//, '');
      const urlText = `${baseUrlForDisplay}/verify/${studyPlan.approval_link}`;
      pdf.text(urlText, margin, yPosition + 7);

      // Update yPosition
      yPosition += 15;
    }
  }

  // Add legal framework text if provided
  if (legalText) {
    checkNewPage(40); // Ensure space for legal text
    yPosition += 20;

    // Section heading
    pdf.setFontSize(13);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(3, 52, 89); // KF Primary color
    pdf.text('Lovhjemmelsgrunnlag', margin, yPosition);
    yPosition += 10;

    // Legal text content
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(60, 60, 60);

    const legalLines = pdf.splitTextToSize(legalText, pageWidth - 2 * margin);
    for (const line of legalLines) {
      checkNewPage(10);
      pdf.text(line, margin, yPosition);
      yPosition += 5;
    }
  }

  // Footer on all pages with blue stripe
  const totalPages = pdf.internal.pages.length - 1; // Subtract 1 because first element is null
  const footerHeight = 15;

  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);

    // Blue footer stripe (matching header)
    pdf.setFillColor(3, 52, 89); // KF Primary color
    pdf.rect(0, pageHeight - footerHeight, pageWidth, footerHeight, 'F');

    // White text on blue background
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(9);

    // Left footer
    pdf.text(
      `© ${new Date().getFullYear()} Kompetanseforbundet`,
      margin,
      pageHeight - 6
    );

    // Center footer
    pdf.text(
      'Godkjent i henhold til EU Micro-credentials Framework og ELMv3',
      pageWidth / 2,
      pageHeight - 6,
      { align: 'center' }
    );

    // Right footer
    pdf.text(
      `Side ${i} av ${totalPages}`,
      pageWidth - margin,
      pageHeight - 6,
      { align: 'right' }
    );
  }

  return pdf.output('blob');
}

/**
 * Download PDF file
 */
export function downloadPDF(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
